import { CascadiaTypographyStyle } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useState } from "react";
import { Button } from "react-aria-components";
import { useTranslation } from "react-i18next";

import {
  Icon,
  Text,
  Tooltip,
  useGetAllFocusShadows,
} from "@certn/cascadia-react";

export interface Props {
  text: string;
  testID: string;
  isHiddenUntilHover?: boolean;
  style?: CascadiaTypographyStyle;
  url?: string;
  isAbsolute?: boolean;
}

export function CopyToClipboard(props: Props) {
  const {
    testID,
    text,
    url,
    isAbsolute,
    style = "form/body-secondary",
  } = props;
  const [isCopied, setIsCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const getAllFocusShadows = useGetAllFocusShadows();
  const { t } = useTranslation();

  const toCopy = url ?? text;

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1200);
    } catch (error) {
      console.warn(error);
      setIsCopied(false);
    }
  };

  const getCopyText = () => {
    if (isCopied) {
      return t("Copied");
    }
    if (url) {
      return t("Copy link");
    }
    return t("Copy text");
  };

  const getCopyIcon = () => {
    if (isCopied) {
      return "check_circle";
    }
    if (url) {
      return "link";
    }
    return "content_copy";
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={css(styles.wrapper, {
        position: isAbsolute ? "relative" : "initial",
      })}
      data-testid={testID}
    >
      <Text style={style} testID={`${testID}-text`}>
        {text}
      </Text>
      <Tooltip
        placement="top"
        description={getCopyText()}
        testID={`${testID}-tooltip`}
      >
        <Button
          onFocusChange={(isFocused) => {
            setIsHovered(isFocused);
          }}
          aria-label="copy-to-clipboard-icon"
          className={css(
            styles.button,
            {
              "&[data-focus-visible]": getAllFocusShadows(),
            },
            isAbsolute && styles.absoluteButton
          )}
          onPress={() => copyToClipboard(toCopy)}
        >
          <Icon
            size="space-4"
            className={css(styles.icon, {
              visibility: isHovered || isCopied ? undefined : "hidden",
            })}
            color={isCopied ? "icon-success" : "icon-weak"}
            name={getCopyIcon()}
            fill={isCopied}
          />
        </Button>
      </Tooltip>
    </div>
  );
}

const styles = {
  wrapper: css({ display: "flex", gap: "var(--space-1)" }),
  button: css({
    padding: "0",
    border: "none",
    background: "none",
    margin: "0",
    outline: "none",
    borderRadius: "var(--border-radius-2)",
    cursor: "pointer",
  }),
  absoluteButton: css({
    position: "absolute",
    right: " -20px",
    height: "-webkit-fill-available",
  }),
  icon: css({ display: "flex", marginLeft: "0.5px" }),
};
